import React, { useState } from "react";
import { Loader2, Check, Trash2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

/** Pexels API key — probed against the live API before it is saved. */
export default function PexelsKeyForm({ cfg, onChange }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  async function save() {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.put("/images/config", { pexels_api_key: key.trim() });
      setKey("");
      setMsg(`Key works · ${r.data.probe?.remaining ?? "?"} requests left this hour`);
      onChange(r.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  async function test() {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.post("/images/config/test");
      setMsg(r.data.ok ? `OK · ${r.data.remaining ?? "?"}/${r.data.limit ?? "?"} requests left this hour` : r.data.error);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  async function remove() {
    if (!window.confirm("Remove the Pexels key? New articles will get branded cards until a key is added again.")) return;
    setBusy(true); setErr(""); setMsg("");
    try { const r = await api.delete("/images/config/pexels-key"); onChange(r.data); setMsg("Key removed"); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {cfg?.pexels_configured ? (
          <span data-testid="pexels-status" className="inline-block border bg-emerald-500/10 text-emerald-400 border-emerald-500/30 mono text-[11px] uppercase tracking-wider px-2 py-1 rounded-sm">
            LIVE · key {cfg.key_hint} · {cfg.key_source}
          </span>
        ) : (
          <span data-testid="pexels-status" className="inline-block border bg-amber-500/10 text-amber-400 border-amber-500/30 mono text-[11px] uppercase tracking-wider px-2 py-1 rounded-sm">
            NO KEY · cards only
          </span>
        )}
        <a href="https://www.pexels.com/api/" target="_blank" rel="noreferrer" className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1 mono">free key <ExternalLink size={10} /></a>
      </div>
      <div className="flex gap-2 max-w-lg">
        <input
          data-testid="pexels-key-input" type="password" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off"
          placeholder={cfg?.pexels_configured ? "paste a new key to replace" : "Pexels API key"}
          className="admin-input flex-1 bg-transparent border border-zinc-800 rounded-sm px-3 py-1.5 text-sm text-zinc-100 mono"
        />
        <button type="button" data-testid="pexels-key-save" disabled={busy || key.trim().length < 20} onClick={save} className="px-3 py-1.5 text-xs font-semibold bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-40 rounded-sm flex items-center gap-1.5">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Test &amp; save
        </button>
        {cfg?.pexels_configured && (
          <>
            <button type="button" data-testid="pexels-key-test" disabled={busy} onClick={test} className="px-3 py-1.5 text-xs border border-zinc-700 rounded-sm hover:border-zinc-400 disabled:opacity-50">Test</button>
            <button type="button" data-testid="pexels-key-remove" disabled={busy || cfg.key_source !== "database"} onClick={remove} title={cfg.key_source !== "database" ? "Key comes from the environment" : ""} className="px-2 py-1.5 text-xs border border-zinc-800 text-zinc-500 hover:text-red-300 hover:border-red-800 rounded-sm disabled:opacity-40"><Trash2 size={12} /></button>
          </>
        )}
      </div>
      {msg && <div data-testid="pexels-key-msg" className="text-xs text-emerald-300 mono">{msg}</div>}
      {err && <div data-testid="pexels-key-error" className="text-xs text-red-400 border border-red-900/60 bg-red-950/40 px-3 py-2 rounded-sm font-mono">{err}</div>}
    </div>
  );
}
